import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useQuran } from '../context/QuranContext';
import { useAuth } from '../context/AuthContext';
import { X, Search, Plus, Trash2, BookOpen, MessageSquare, ArrowRight, Book, Edit3, Check, Sparkles, Loader2, Copy } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import LoginPromptDialog from './LoginPromptDialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Legacy ROOT_MEANINGS_DB removed: Now using full Lane's Lexicon dataset via MongoDB

const WordAnalysisPanel = ({ word, isOpen, onClose }) => {
    const { goToVerse, surahs, startRootBrowsing } = useQuran();
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [loginPromptFeature, setLoginPromptFeature] = useState('');
    // Data states
    const [relatedVerses, setRelatedVerses] = useState([]);
    const [customMeanings, setCustomMeanings] = useState([]);
    const [rootBasedMeanings, setRootBasedMeanings] = useState([]);  // Meanings from same root, different words
    const [lexiconData, setLexiconData] = useState(null); // Lane's Lexicon data
    const [loading, setLoading] = useState(false);
    const [loadingMeanings, setLoadingMeanings] = useState(false);
    const [loadingLexicon, setLoadingLexicon] = useState(false);

    // Form states
    const [showAddMeaning, setShowAddMeaning] = useState(false);
    const [newMeaning, setNewMeaning] = useState('');
    const [newReasoning, setNewReasoning] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    // Edit states
    const [editingMeaningId, setEditingMeaningId] = useState(null);
    const [editMeaning, setEditMeaning] = useState('');
    const [editReasoning, setEditReasoning] = useState('');

    // State for root information
    const [rootInfo, setRootInfo] = useState(null);
    const [wordRoot, setWordRoot] = useState(null);
    const [wordGrammar, setWordGrammar] = useState(null);

    // AI meaning generation states
    const [isGeneratingAIMeaning, setIsGeneratingAIMeaning] = useState(false);
    const [aiGeneratedMeaning, setAiGeneratedMeaning] = useState(null);

    // Root-based verse view states (replacing form-based)
    const [rootVersesPage, setRootVersesPage] = useState(1);
    const [rootVersesTotalPages, setRootVersesTotalPages] = useState(1);
    const [loadingMoreVerses, setLoadingMoreVerses] = useState(false);

    // Extract root text from word
    const rootText = word?.text_uthmani || word?.text_indopak || word?.text || '';

    // Get consonantal root for lexicon lookup
    const getConsonantalRoot = (text) => {
        if (!text) return '';
        return text
            .replace(/[\u064B-\u065F\u0670\u0671]/g, '')
            .replace(/[\u0622\u0623\u0625\u0627]/g, 'ا')
            .replace(/[\u0649\u064A]/g, 'ي')
            .replace(/\u0629/g, 'ة')
            .trim();
    };

    const consonantalRoot = getConsonantalRoot(rootText);

    // Format text from Lane's Lexicon
    const formatLaneDefinition = (text) => {
        if (!text) return null;
        // Basic formatting for Lane's dense text
        return text.split('-b2-').map((part, i) => (
            <p key={i} className="mb-2 leading-relaxed text-sm text-[#cbd5e1]">
                {part.trim()}
            </p>
        ));
    };

    // Load custom meanings for this word ID (no root needed - loads immediately)
    const loadWordMeanings = useCallback(async () => {
        if (!word?.id) return;
        if (!isAuthenticated) {
            setCustomMeanings([]);
            return;
        }

        setLoadingMeanings(true);
        try {
            const response = await axios.get(`${API}/custom-meanings/${word.id}`, {
                headers: getAuthHeaders()
            });
            setCustomMeanings(response.data.meanings || []);
        } catch (err) {
            console.error('Failed to load custom meanings:', err);
            setCustomMeanings([]);
        } finally {
            setLoadingMeanings(false);
        }
    }, [word?.id, isAuthenticated, getAuthHeaders]);

    // Load meanings from other words sharing the same root (called after root is discovered)
    const loadRootBasedMeanings = useCallback(async (root) => {
        if (!word?.id || !root || !isAuthenticated) {
            setRootBasedMeanings([]);
            return;
        }

        try {
            const rootResponse = await axios.get(`${API}/custom-meanings/by-root/${encodeURIComponent(root)}`, {
                headers: getAuthHeaders()
            });
            const allRootMeanings = rootResponse.data.meanings || [];
            // Filter out meanings that belong to this word
            const otherMeanings = allRootMeanings.filter(m => m.root_id !== word.id);
            setRootBasedMeanings(otherMeanings);
        } catch (err) {
            console.error('Failed to load root-based meanings:', err);
            setRootBasedMeanings([]);
        }
    }, [word?.id, isAuthenticated, getAuthHeaders]);

    // Load full Lane's Lexicon data from backend
    const loadLexiconData = useCallback(async (root) => {
        if (!root) {
            setLexiconData(null);
            return;
        }
        setLoadingLexicon(true);
        try {
            const response = await axios.get(`${API}/lexicon/root/${encodeURIComponent(root)}`);
            if (response.data.found) {
                setLexiconData(response.data.data);
            } else {
                setLexiconData(null);
            }
        } catch (err) {
            console.error('Failed to load lexicon data:', err);
            setLexiconData(null);
        } finally {
            setLoadingLexicon(false);
        }
    }, []);

    // Fetch original flat list of verses for the root, but with pagination
    const loadRootVerses = useCallback(async (rootStr, page = 1) => {
        if (!rootStr) return;

        setLoadingMoreVerses(true);
        if (page === 1) {
            setRelatedVerses([]); // reset on new root
        }

        try {
            const rootResponse = await axios.get(`${API}/morphology/root/${encodeURIComponent(rootStr)}`, {
                params: { page }
            });

            if (rootResponse.data) {
                if (page === 1) {
                    const info = rootResponse.data.root_info || {};
                    // Strict Wildcard Check: If stats match total Quran (77k+ words, 114 surahs)
                    // we suppress this as a 'ghost root' often returned for non-root words
                    if ((info.total_occurrences || 0) >= 77000 && (info.surahs_count || 0) === 114) {
                        setRootInfo(null);
                        setRelatedVerses([]);
                        setWordRoot(null); // Clear the root if it was a wildcard
                        return;
                    }
                    setRootInfo(info);
                    setRelatedVerses(rootResponse.data.results || []);
                } else {
                    setRelatedVerses(prev => {
                        // Avoid duplicates if user clicks multiple times quickly
                        const existingKeys = new Set(prev.map(v => v.verse_key));
                        const newVerses = (rootResponse.data.results || []).filter(v => !existingKeys.has(v.verse_key));
                        return [...prev, ...newVerses];
                    });
                }

                // Calculate total pages based on limit 60 in backend
                const total = rootResponse.data.total_occurrences || 0;
                const calculatedTotalPages = Math.max(1, Math.ceil(total / 60));
                setRootVersesTotalPages(Math.max(calculatedTotalPages, page));
                setRootVersesPage(page);
            }
        } catch (err) {
            console.error('Failed to load root verses:', err);
        } finally {
            setLoadingMoreVerses(false);
        }
    }, []);

    // Helper to check auth before protected action
    const requireAuth = (featureName) => {
        if (!isAuthenticated) {
            setLoginPromptFeature(featureName);
            setShowLoginPrompt(true);
            return false;
        }
        return true;
    };

    // Normalize Arabic text by removing all diacritics for root search
    const normalizeArabicForSearch = (text) => {
        if (!text) return '';
        return text
            // Remove all Arabic diacritics (tashkeel)
            .replace(/[\u064B-\u065F]/g, '') // Fatha, Damma, Kasra, Shadda, Sukun, etc.
            .replace(/\u0670/g, '') // Superscript Alef
            .replace(/\u0671/g, 'ا') // Alef Wasla -> Alef
            .replace(/[\u0622\u0623\u0625]/g, 'ا') // Alef with Madda/Hamza -> Alef
            .replace(/\u0624/g, 'و') // Waw with Hamza -> Waw
            .replace(/\u0626/g, 'ي') // Yeh with Hamza -> Yeh
            .replace(/\u0649/g, 'ي') // Alef Maksura -> Yeh
            .replace(/\u06D6/g, '') // Small high ligature sad with lam with alef maksura
            .replace(/[\u06D7-\u06ED]/g, '') // Other Quranic annotations
            .replace(/\u0640/g, '') // Tatweel (kashida)
            .trim();
    };

    // Search for related verses using QuranHub morphology API
    useEffect(() => {
        if (!isOpen || !word) return;

        // Load custom meanings immediately (no root needed)
        loadWordMeanings();

        const searchRelatedVerses = async () => {
            setLoading(true);
            setRootInfo(null);
            setWordRoot(null);

            try {
                // First, get the word's root using its location (surah:verse:position)
                const location = word.location || `${word.verse_key || ''}:${word.position || ''}`;

                if (location && location.split(':').length >= 3) {
                    // Use QuranHub API to get morphology and root
                    const morphResponse = await axios.get(`${API}/morphology/word/${location}`);

                    if (morphResponse.data?.data?.morphology) {
                        setWordGrammar(morphResponse.data.data.morphology.all_tags || []);

                        // Get the root from the morphology data
                        const rootTags = morphResponse.data.data.morphology.tags_by_category?.Root || [];
                        // First root tag is usually the main root
                        const mainRoot = rootTags[0]?.code;

                        if (mainRoot) {
                            setWordRoot(mainRoot);

                            // Load root-based meanings as soon as root is known (don't wait for root search)
                            loadRootBasedMeanings(mainRoot);
                            // Load Lane's Lexicon dict entry
                            loadLexiconData(mainRoot);

                            // Now search for all verses with this root (this is the slow part)
                            await loadRootVerses(mainRoot, 1);
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to search verses with morphology:', err);
            } finally {
                setLoading(false);
            }
        };

        searchRelatedVerses();
    }, [isOpen, word, loadWordMeanings, loadRootBasedMeanings, loadLexiconData, loadRootVerses]);

    // Reset form when panel closes
    useEffect(() => {
        if (!isOpen) {
            setShowAddMeaning(false);
            setNewMeaning('');
            setNewReasoning('');
            setEditingMeaningId(null);
            setEditMeaning('');
            setEditReasoning('');
            setRootBasedMeanings([]);
            setAiGeneratedMeaning(null);
            setLexiconData(null);
        }
    }, [isOpen]);

    const handleVerseClick = (verseKey, index) => {
        const [surahId, verseNum] = verseKey.split(':').map(Number);
        startRootBrowsing(word, relatedVerses, index);
        goToVerse(surahId, verseNum);
        onClose();
    };

    const handleAddMeaning = async () => {
        if (!requireAuth('Custom Word Meanings')) return;
        if (!newMeaning.trim()) {
            toast.error('Please enter a meaning');
            return;
        }

        setIsSaving(true);
        try {
            // Construct word location (surah:verse:position)
            const wordLocation = word.location || `${word.verse_key || ''}:${word.position || ''}`;

            const response = await axios.post(`${API}/custom-meanings`, {
                root_id: word.id || 0,
                root_text: rootText,
                root: wordRoot,
                word_location: wordLocation.split(':').length >= 3 ? wordLocation : null,
                meaning: newMeaning.trim(),
                reasoning: newReasoning.trim() || null
            }, {
                headers: getAuthHeaders()
            });

            setCustomMeanings(prev => [...prev, response.data]);
            setNewMeaning('');
            setNewReasoning('');
            setShowAddMeaning(false);
            toast.success('Custom meaning added');
        } catch (err) {
            console.error('Failed to add meaning:', err);
            toast.error('Failed to add meaning. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleStartEdit = (meaning) => {
        setEditingMeaningId(meaning.id);
        setEditMeaning(meaning.meaning);
        setEditReasoning(meaning.reasoning || '');
    };

    const handleCancelEdit = () => {
        setEditingMeaningId(null);
        setEditMeaning('');
        setEditReasoning('');
    };

    const handleSaveEdit = async (meaningId) => {
        if (!editMeaning.trim()) {
            toast.error('Please enter a meaning');
            return;
        }

        setIsSaving(true);
        try {
            await axios.put(`${API}/custom-meanings/${meaningId}`, {
                meaning: editMeaning.trim(),
                reasoning: editReasoning.trim() || null
            }, {
                headers: getAuthHeaders()
            });

            setCustomMeanings(prev => prev.map(m =>
                m.id === meaningId
                    ? { ...m, meaning: editMeaning.trim(), reasoning: editReasoning.trim() || null }
                    : m
            ));
            setEditingMeaningId(null);
            setEditMeaning('');
            setEditReasoning('');
            toast.success('Meaning updated');
        } catch (err) {
            console.error('Failed to update meaning:', err);
            toast.error('Failed to update meaning. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteMeaning = async (meaningId) => {
        try {
            await axios.delete(`${API}/custom-meanings/${meaningId}`, {
                headers: getAuthHeaders()
            });
            setCustomMeanings(prev => prev.filter(m => m.id !== meaningId));
            toast.success('Meaning deleted');
        } catch (err) {
            console.error('Failed to delete meaning:', err);
            toast.error('Failed to delete meaning');
        }
    };

    const handleCancelAdd = () => {
        setNewMeaning('');
        setNewReasoning('');
        setShowAddMeaning(false);
    };

    // AI-powered meaning generation from Quranic contexts
    const handleGenerateAIMeaning = async () => {
        if (!requireAuth('AI Word Meaning')) return;
        setIsGeneratingAIMeaning(true);
        setAiGeneratedMeaning(null);

        try {
            const wordLocation = word.location || `${word.verse_key || ''}:${word.position || ''}`;

            const response = await axios.post(`${API}/ai/generate-word-meaning`, {
                word_text: rootText,
                root: wordRoot || null,
                word_location: wordLocation,
                transliteration: word.transliteration?.text || '',
                standard_meaning: word.translation?.text || '',
                parsed_verses: relatedVerses.map(v => ({
                    verse_key: v.verse_key,
                    surah_name: v.surah_name || '',
                    word_text: v.word_text || getConsonantalRoot(word?.text || '')
                }))
            }, {
                headers: getAuthHeaders()
            });

            setAiGeneratedMeaning({
                meaning: response.data.meaning,
                reasoning: response.data.reasoning
            });

            toast.success('AI meaning generated from Quranic contexts');
        } catch (err) {
            console.error('Failed to generate AI meaning:', err);
            const errorMsg = err.response?.data?.detail || 'Failed to generate AI meaning. Please try again.';
            toast.error(errorMsg);
        } finally {
            setIsGeneratingAIMeaning(false);
        }
    };

    // Save AI-generated meaning as custom meaning
    const handleSaveAIMeaning = async () => {
        if (!aiGeneratedMeaning) return;

        setIsSaving(true);
        try {
            const wordLocation = word.location || `${word.verse_key || ''}:${word.position || ''}`;

            const response = await axios.post(`${API}/custom-meanings`, {
                root_id: word.id || 0,
                root_text: rootText,
                root: wordRoot,
                word_location: wordLocation.split(':').length >= 3 ? wordLocation : null,
                meaning: aiGeneratedMeaning.meaning,
                reasoning: aiGeneratedMeaning.reasoning
            }, {
                headers: getAuthHeaders()
            });

            setCustomMeanings(prev => [...prev, response.data]);
            setAiGeneratedMeaning(null);
            toast.success('AI meaning saved as custom meaning');
        } catch (err) {
            console.error('Failed to save AI meaning:', err);
            toast.error('Failed to save meaning. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscardAIMeaning = () => {
        setAiGeneratedMeaning(null);
    };

    const getSurahName = (surahId) => {
        const surah = surahs.find(s => s.id === surahId);
        return surah ? surah.name_simple : `Surah ${surahId}`;
    };

    // Highlight word in verse text - using normalized matching
    const highlightWords = (text, searchWordsArray) => {
        if (!text || !searchWordsArray || !searchWordsArray.length) return text;

        let resultText = text;
        const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        searchWordsArray.forEach(searchWord => {
            const normalizedSearch = normalizeArabicForSearch(searchWord);
            if (!normalizedSearch) return;

            const pattern = normalizedSearch.split('').map(char => {
                let charPattern = escapeRegex(char);
                if (char === 'ا') charPattern = '[اأإآ]';
                else if (char === 'و') charPattern = '[وؤ]';
                else if (char === 'ي') charPattern = '[يئى]';
                else if (char === 'ة' || char === 'ه') charPattern = '[ةه]';
                
                return charPattern + '[\u064B-\u065F\u0670\u06D6-\u06ED]*';
            }).join('');

            try {
                // Negative lookahead to avoid re-highlighting already highlighted text within tags
                const regex = new RegExp(`(${pattern})(?![^<]*>)`, 'gi');
                resultText = resultText.replace(regex, '<mark class="highlight-word">$1</mark>');
            } catch (e) {
                // ignore errors for invalid patterns
            }
        });
        return resultText;
    };

    // Copy to clipboard helper
    const copyToClipboard = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy to clipboard');
        }
    };

    // Format meaning with reasoning for copying
    const formatMeaningForCopy = (meaning, reasoning) => {
        let text = `Meaning: ${meaning}`;
        if (reasoning) {
            text += `\nReasoning: ${reasoning}`;
        }
        return text;
    };

    // Use normalized search term for highlighting
    const searchWord = rootText;

    const renderGrammarBreakdown = () => {
        if (!wordGrammar || wordGrammar.length === 0) return null;

        const grammarMap = { pos: null, gender: null, number: null, case: null, tense: null, prefix: null, arabic: [] };

        wordGrammar.forEach(tag => {
            const c = tag.code;
            const cat = tag.category;
            const m = tag.user_meaning || tag.meaning || '';

            if (['Asma', 'Afaal', 'Irab', 'Aam', 'Sawabiq'].includes(cat) && m) {
                if (!grammarMap.arabic.includes(m)) grammarMap.arabic.push(m);
            }

            if (cat === 'Asma') grammarMap.pos = 'N – Noun';
            else if (cat === 'Afaal') {
                grammarMap.pos = 'V – Verb';
                if (m.includes('ماض')) grammarMap.tense = 'past';
                if (m.includes('مضارع')) grammarMap.tense = 'present';
                if (m.includes('أمر')) grammarMap.tense = 'imperative';
            } else if (cat === 'Sawabiq') grammarMap.prefix = `P – Prefix`;

            if (cat === 'Aam') {
                if (c === 'ذك') grammarMap.gender = 'masculine';
                if (c === 'نث') grammarMap.gender = 'feminine';
                if (c === 'رد') grammarMap.number = 'singular';
                if (c === 'مث') grammarMap.number = 'dual';
                if (c === 'جم') grammarMap.number = 'plural';
            } else if (cat === 'Irab') {
                if (c === 'جر' || m.includes('مجرور')) grammarMap.case = 'genitive';
                if (c === 'رف' || m.includes('مرفوع')) grammarMap.case = 'nominative';
                if (c === 'نص' || m.includes('منصوب')) grammarMap.case = 'accusative';
            }
        });

        const posSplit = grammarMap.pos ? grammarMap.pos.split('–') : ['PART', ' Particle'];
        const posSym = posSplit[0].trim();
        const posName = posSplit[1]?.trim().toLowerCase() || '';
        const traits = [grammarMap.case, grammarMap.gender, grammarMap.number, grammarMap.tense].filter(Boolean).join(' ');
        const desc = traits ? `${traits} ${posName}` : posName;

        return (
            <div className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#1e293b]">
                <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-[#38bdf8]" />
                    <p className="text-xs uppercase tracking-[0.2em] text-[#38bdf8] ui-text font-medium">
                        Grammar Breakdown
                    </p>
                </div>
                <div className="space-y-2 mt-2">
                    {grammarMap.prefix && (
                        <div className="flex justify-between items-center text-sm border-b border-[#1e293b]/50 pb-2">
                            <span className="text-[#ef4444] font-bold font-mono text-base">{grammarMap.prefix.split('–')[0]}</span>
                            <span className="text-[#e2e8f0]">prefixed particle/preposition</span>
                        </div>
                    )}
                    <div className="flex justify-between items-center text-sm border-b border-[#1e293b]/50 pb-2">
                        <span className="text-[#3b82f6] font-mono font-bold text-base mr-4">{posSym}</span>
                        <div className="text-right flex-1">
                            <span className="text-[#e2e8f0] block capitalize">{desc || 'Word'}</span>
                        </div>
                    </div>
                    {grammarMap.arabic.length > 0 && (
                        <div className="pt-2 text-right">
                            <p className="text-[#10b981] font-bold text-sm leading-relaxed" dir="rtl">
                                {grammarMap.arabic.filter(x => x.length < 25).slice(0, 3).join(' · ')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (!word) return null;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent
                    className="w-full sm:max-w-2xl bg-[#09090b] border-[#1e293b] p-0 overflow-hidden max-h-[85vh]"
                    data-testid="word-analysis-panel"
                >
                    <DialogHeader className="p-4 sm:p-6 border-b border-[#1e293b]">
                        <DialogTitle className="text-[#f8fafc] heading-primary text-xl sm:text-2xl flex items-center gap-3">
                            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4af37]" />
                            Word Analysis
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="h-[calc(85vh-80px)] min-h-[50vh]">
                        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                            {/* Word Display */}
                            <div className="text-center p-4 sm:p-6 bg-[#0f172a]/50 rounded-lg border border-[#1e293b]">
                                <div className="flex items-center justify-center gap-3 mb-2 sm:mb-3">
                                    <p
                                        className="arabic-text text-3xl sm:text-4xl text-[#d4af37]"
                                        dir="rtl"
                                        lang="ar"
                                        data-testid="analysis-arabic-word"
                                    >
                                        {rootText}
                                    </p>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => copyToClipboard(rootText, 'Arabic word')}
                                        className="text-[#64748b] hover:text-[#f8fafc] hover:bg-[#1e293b] h-8 w-8 p-0 shrink-0 mt-1"
                                        title="Copy Arabic text"
                                        data-testid="copy-arabic-word"
                                    >
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>
                                {word.transliteration?.text && (
                                    <p className="text-[#94a3b8] italic ui-text mb-1 sm:mb-2 text-sm sm:text-base">
                                        {word.transliteration.text}
                                    </p>
                                )}
                                {word.translation?.text && (
                                    <p className="text-[#f8fafc] ui-text text-base sm:text-lg">
                                        {word.translation.text}
                                    </p>
                                )}
                            </div>

                            {/* Tabs */}
                            <Tabs defaultValue="dictionary" className="w-full">
                                <TabsList className="w-full bg-[#0f172a] border border-[#1e293b]">
                                    <TabsTrigger
                                        value="dictionary"
                                        className="flex-1 data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-sm"
                                        data-testid="tab-dictionary"
                                    >
                                        <Book className="w-4 h-4 mr-1 sm:mr-2" />
                                        Dictionary
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="ai-custom"
                                        className="flex-1 data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-sm"
                                        data-testid="tab-ai-custom"
                                    >
                                        <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
                                        Ai / Custom
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value="verses"
                                        className="flex-1 data-[state=active]:bg-[#1e293b] data-[state=active]:text-[#d4af37] text-sm"
                                        data-testid="tab-related-verses"
                                    >
                                        <Search className="w-4 h-4 mr-1 sm:mr-2" />
                                        Verses {relatedVerses.length > 0 ? `(${rootVersesPage < rootVersesTotalPages ? `${relatedVerses.length}+` : relatedVerses.length})` : ''}
                                    </TabsTrigger>
                                </TabsList>

                                {/* Dictionary Tab */}
                                <TabsContent value="dictionary" className="mt-4">
                                    <div className="space-y-4">
                                        {renderGrammarBreakdown()}

                                        {/* Lexicon Meanings */}
                                        {loadingLexicon ? (
                                            <div className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#1e293b]">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Book className="w-4 h-4 text-[#d4af37]" />
                                                    <Skeleton className="h-4 w-40 bg-[#1e293b]" />
                                                </div>
                                                <Skeleton className="h-16 w-full bg-[#1e293b]" />
                                            </div>
                                        ) : lexiconData && (
                                            <div className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#1e293b]">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Book className="w-4 h-4 text-[#d4af37]" />
                                                    <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] ui-text font-medium">
                                                        Lane's Lexicon
                                                    </p>
                                                </div>

                                                {lexiconData.summary_en && (
                                                    <div className="mb-4">
                                                        <p className="text-[#f8fafc] text-sm leading-relaxed">
                                                            {lexiconData.summary_en}
                                                        </p>
                                                    </div>
                                                )}

                                                {lexiconData.definition_en && (
                                                    <div className="mt-3 pt-3 border-t border-[#1e293b]">
                                                        <p className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] mb-2 ui-text">
                                                            Classical Definition
                                                        </p>
                                                        <div className="text-[#cbd5e1]">
                                                            {formatLaneDefinition(lexiconData.definition_en)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>

                                {/* AI / Custom Tab */}
                                <TabsContent value="ai-custom" className="mt-4">
                                    <div className="space-y-4">
                                        {/* Action Buttons Row */}
                                        {!showAddMeaning && !aiGeneratedMeaning && (
                                            <div className="space-y-3">
                                                {/* AI Generate Meaning Button */}
                                                <Button
                                                    onClick={handleGenerateAIMeaning}
                                                    disabled={isGeneratingAIMeaning || loading}
                                                    className="w-full bg-gradient-to-r from-[#8b5cf6] to-[#6366f1] text-white font-semibold hover:from-[#a78bfa] hover:to-[#818cf8] h-12"
                                                    data-testid="ai-generate-meaning-btn"
                                                >
                                                    {isGeneratingAIMeaning ? (
                                                        <>
                                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                            Analyzing {relatedVerses.length} Quranic Contexts...
                                                        </>
                                                    ) : loading ? (
                                                        <>
                                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                            Loading word data...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Sparkles className="w-5 h-5 mr-2" />
                                                            AI: Derive Meaning from Quran
                                                        </>
                                                    )}
                                                </Button>
                                                {wordRoot ? (
                                                    <p className="text-xs text-center text-[#64748b]">
                                                        AI will analyze {relatedVerses.length} verses with root <span className="text-[#d4af37] arabic-text" dir="rtl">{wordRoot}</span> to derive meaning
                                                    </p>
                                                ) : !loading && (
                                                    <p className="text-xs text-center text-[#f59e0b]">
                                                        Root lookup unavailable. AI will use word text for analysis.
                                                    </p>
                                                )}

                                                {/* Add Custom Meaning Button */}
                                                <Button
                                                    onClick={() => setShowAddMeaning(true)}
                                                    variant="outline"
                                                    className="w-full border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10 h-11"
                                                    data-testid="add-meaning-btn"
                                                >
                                                    <Plus className="w-5 h-5 mr-2" />
                                                    Add Your Custom Meaning
                                                </Button>
                                            </div>
                                        )}

                                        {/* AI Generated Meaning Display */}
                                        {aiGeneratedMeaning && (
                                            <div className="p-4 bg-gradient-to-br from-[#8b5cf6]/10 to-[#6366f1]/10 rounded-lg border-2 border-[#8b5cf6] space-y-4">
                                                <div className="flex items-center gap-2">
                                                    <Sparkles className="w-5 h-5 text-[#8b5cf6]" />
                                                    <p className="text-[#8b5cf6] font-medium">AI-Derived Meaning from Quranic Context</p>
                                                </div>

                                                <div className="p-3 bg-[#0f172a]/50 rounded-lg">
                                                    <p className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] mb-2">Derived Meaning</p>
                                                    <p className="text-[#f8fafc] text-lg">{aiGeneratedMeaning.meaning}</p>
                                                </div>

                                                <div className="p-3 bg-[#0f172a]/50 rounded-lg">
                                                    <p className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] mb-2">Reasoning (from Quranic analysis)</p>
                                                    <p className="text-[#94a3b8] text-sm whitespace-pre-wrap">{aiGeneratedMeaning.reasoning}</p>
                                                </div>

                                                <div className="flex gap-2 justify-end">
                                                    <Button
                                                        variant="outline"
                                                        onClick={handleDiscardAIMeaning}
                                                        className="border-[#475569] text-[#94a3b8]"
                                                        disabled={isSaving}
                                                    >
                                                        <X className="w-4 h-4 mr-1" />
                                                        Discard
                                                    </Button>
                                                    <Button
                                                        onClick={handleSaveAIMeaning}
                                                        className="bg-[#8b5cf6] text-white hover:bg-[#a78bfa] font-semibold"
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? (
                                                            <>
                                                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Check className="w-4 h-4 mr-1" />
                                                                Save as Custom Meaning
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Add New Meaning Form */}
                                        {showAddMeaning && (
                                            <div className="p-4 bg-[#0f172a]/50 rounded-lg border-2 border-[#d4af37] space-y-3">
                                                <p className="text-[#d4af37] font-medium text-sm">Add Your Custom Meaning</p>
                                                <Input
                                                    placeholder="Enter your custom meaning..."
                                                    value={newMeaning}
                                                    onChange={(e) => setNewMeaning(e.target.value)}
                                                    className="bg-[#09090b] border-[#1e293b] text-[#f8fafc] h-11"
                                                    data-testid="new-meaning-input"
                                                    autoFocus
                                                    disabled={isSaving}
                                                />
                                                <Textarea
                                                    placeholder="Why do you think this meaning? (optional reasoning)"
                                                    value={newReasoning}
                                                    onChange={(e) => setNewReasoning(e.target.value)}
                                                    className="bg-[#09090b] border-[#1e293b] text-[#f8fafc] min-h-[80px]"
                                                    data-testid="new-reasoning-input"
                                                    disabled={isSaving}
                                                />
                                                <div className="flex gap-2 justify-end">
                                                    <Button
                                                        variant="outline"
                                                        onClick={handleCancelAdd}
                                                        className="border-[#1e293b] text-[#94a3b8]"
                                                        data-testid="cancel-meaning-btn"
                                                        disabled={isSaving}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        onClick={handleAddMeaning}
                                                        className="bg-[#d4af37] text-[#02040a] hover:bg-[#facc15] font-semibold"
                                                        data-testid="save-meaning-btn"
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? 'Saving...' : 'Save Meaning'}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Custom Meanings List */}
                                        {loadingMeanings ? (
                                            <div className="space-y-3">
                                                <Skeleton className="h-20 w-full bg-[#1e293b]" />
                                            </div>
                                        ) : customMeanings.length > 0 && (
                                            <div className="space-y-3" data-testid="custom-meanings-list">
                                                <p className="text-xs uppercase tracking-[0.2em] text-[#94a3b8] ui-text">
                                                    Your Custom Meanings ({customMeanings.length})
                                                </p>
                                                {customMeanings.map((meaning) => (
                                                    <div
                                                        key={meaning.id}
                                                        className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#d4af37]/30"
                                                    >
                                                        {editingMeaningId === meaning.id ? (
                                                            /* Edit Mode */
                                                            <div className="space-y-3">
                                                                <Input
                                                                    value={editMeaning}
                                                                    onChange={(e) => setEditMeaning(e.target.value)}
                                                                    className="bg-[#09090b] border-[#1e293b] text-[#f8fafc] h-10"
                                                                    disabled={isSaving}
                                                                    autoFocus
                                                                />
                                                                <Textarea
                                                                    value={editReasoning}
                                                                    onChange={(e) => setEditReasoning(e.target.value)}
                                                                    placeholder="Reasoning (optional)"
                                                                    className="bg-[#09090b] border-[#1e293b] text-[#f8fafc] min-h-[60px]"
                                                                    disabled={isSaving}
                                                                />
                                                                <div className="flex gap-2 justify-end">
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        onClick={handleCancelEdit}
                                                                        className="border-[#1e293b] text-[#94a3b8]"
                                                                        disabled={isSaving}
                                                                    >
                                                                        Cancel
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => handleSaveEdit(meaning.id)}
                                                                        className="bg-[#10b981] hover:bg-[#059669] text-white"
                                                                        disabled={isSaving}
                                                                    >
                                                                        <Check className="w-4 h-4 mr-1" />
                                                                        {isSaving ? 'Saving...' : 'Save'}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            /* View Mode */
                                                            <div className="block">
                                                                <div className="float-right flex gap-1 ml-3 mb-1">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => copyToClipboard(
                                                                            formatMeaningForCopy(meaning.meaning, meaning.reasoning),
                                                                            'Custom meaning'
                                                                        )}
                                                                        className="text-[#64748b] hover:text-[#f8fafc] hover:bg-[#1e293b] h-8 w-8 p-0"
                                                                        data-testid={`copy-meaning-${meaning.id}`}
                                                                    >
                                                                        <Copy className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleStartEdit(meaning)}
                                                                        className="text-[#d4af37] hover:text-[#facc15] hover:bg-[#d4af37]/10 h-8 w-8 p-0"
                                                                        data-testid={`edit-meaning-${meaning.id}`}
                                                                    >
                                                                        <Edit3 className="w-4 h-4" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteMeaning(meaning.id)}
                                                                        className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10 h-8 w-8 p-0"
                                                                        data-testid={`delete-meaning-${meaning.id}`}
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </Button>
                                                                </div>
                                                                <p className="text-[#f8fafc] mb-2">{meaning.meaning}</p>
                                                                {meaning.reasoning && (
                                                                    <p className="text-[#94a3b8] text-sm italic whitespace-pre-wrap clear-both mt-2">
                                                                        Reasoning: {meaning.reasoning}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {/* Root-Based Meanings from Other Words (Same Root, Different Verses) */}
                                        {rootBasedMeanings.length > 0 && (
                                            <div className="space-y-3" data-testid="root-based-meanings-list">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-xs uppercase tracking-[0.2em] text-[#10b981] ui-text">
                                                        Meanings from Same Root
                                                    </p>
                                                    {wordRoot && (
                                                        <span className="text-[#d4af37] arabic-text text-sm" dir="rtl">({wordRoot})</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-[#64748b] -mt-2">
                                                    Custom meanings you defined for other words sharing this root
                                                </p>
                                                {rootBasedMeanings.map((meaning) => (
                                                    <div
                                                        key={meaning.id}
                                                        className="p-4 bg-[#0f172a]/50 rounded-lg border border-[#10b981]/30"
                                                    >
                                                        <div className="block">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => copyToClipboard(
                                                                    formatMeaningForCopy(meaning.meaning, meaning.reasoning),
                                                                    'Root meaning'
                                                                )}
                                                                className="float-right ml-3 mb-1 text-[#64748b] hover:text-[#f8fafc] hover:bg-[#1e293b] h-8 w-8 p-0"
                                                                data-testid={`copy-root-meaning-${meaning.id}`}
                                                            >
                                                                <Copy className="w-4 h-4" />
                                                            </Button>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-[#d4af37] arabic-text text-lg" dir="rtl">
                                                                    {meaning.root_text}
                                                                </span>
                                                                {meaning.word_location && (
                                                                    <span className="text-[#64748b] text-xs">
                                                                        ({meaning.word_location.split(':').slice(0, 2).join(':')})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[#f8fafc] mb-2">{meaning.meaning}</p>
                                                            {meaning.reasoning && (
                                                                <p className="text-[#94a3b8] text-sm italic whitespace-pre-wrap clear-both mt-2">
                                                                    Reasoning: {meaning.reasoning}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </TabsContent>

                                {/* Related Verses Tab */}
                                <TabsContent value="verses" className="mt-4">
                                    <div className="space-y-4">
                                        {/* Root Information Banner */}
                                        {wordRoot && rootInfo && (
                                            <div className="p-3 bg-gradient-to-r from-[#d4af37]/10 to-[#a16207]/10 rounded-lg border border-[#d4af37]/30">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[#d4af37] text-lg arabic-text" dir="rtl">{wordRoot}</span>
                                                        <span className="text-[#94a3b8] text-sm">Root</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="text-[#d4af37] font-bold">{rootInfo.total_occurrences || relatedVerses.length}</span>
                                                        <span className="text-[#94a3b8] text-sm ml-1">occurrences</span>
                                                        {rootInfo.surahs_count && (
                                                            <span className="text-[#64748b] text-xs ml-2">in {rootInfo.surahs_count} surahs</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Verses List */}
                                        <p className="text-xs text-[#94a3b8] text-center px-2">
                                            {wordRoot ? (
                                                <>All verses containing words derived from root <span className="text-[#d4af37] arabic-text" dir="rtl">{wordRoot}</span>. Tap to view.</>
                                            ) : (
                                                <>Tap a verse to view it. <span className="text-[#d4af37] font-medium">Gold highlighted</span> words show matches.</>
                                            )}
                                        </p>

                                        {loading && rootVersesPage === 1 ? (
                                            <div className="space-y-3">
                                                {[1, 2, 3].map(i => (
                                                    <Skeleton key={i} className="h-24 w-full bg-[#1e293b]" />
                                                ))}
                                            </div>
                                        ) : relatedVerses.length > 0 ? (
                                            <div className="space-y-3 pb-6" data-testid="related-verses-list">
                                                {relatedVerses.map((result, index) => {
                                                    const surahId = parseInt(result.verse_key.split(':')[0]);
                                                    const surah = surahs?.find(s => s.id === surahId);
                                                    const surahName = surah ? surah.name_simple : (result.surah_name || `Surah ${surahId}`);
                                                    // determine search terms for highlighter
                                                    const searchWords = result.word_texts || (result.word_text ? [result.word_text] : [getConsonantalRoot(word?.text || '')]);

                                                    return (
                                                        <div
                                                            key={index}
                                                            className="p-3 sm:p-4 rounded-lg border border-[#1e293b] hover:border-[#d4af37]/50 transition-colors cursor-pointer bg-[#0f172a]/30 active:bg-[#1e293b]"
                                                            onClick={() => handleVerseClick(result.verse_key, index)}
                                                            data-testid={`related-verse-${index}`}
                                                        >
                                                            <div className="flex items-center justify-between mb-2">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="badge-gold text-xs">
                                                                        {surahName}
                                                                    </span>
                                                                    <span className="text-[#94a3b8] text-sm ui-text">
                                                                        {result.verse_key}
                                                                    </span>
                                                                    {searchWords.length > 0 && (
                                                                        <span className="text-[#d4af37] text-sm arabic-text" dir="rtl">
                                                                            ({searchWords.join('، ')})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <ArrowRight className="w-4 h-4 text-[#475569]" />
                                                            </div>
                                                            <p
                                                                className="text-[#e2e8f0] text-sm leading-relaxed verse-highlight-container arabic-text"
                                                                dir="rtl"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: highlightWords(result.text, searchWords)
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                })}

                                                {/* Pagination Button */}
                                                {rootVersesPage < rootVersesTotalPages && (
                                                    <Button
                                                        variant="outline"
                                                        className="w-full mt-4 border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b] hover:text-[#f8fafc]"
                                                        onClick={() => loadRootVerses(wordRoot || consonantalRoot, rootVersesPage + 1)}
                                                        disabled={loadingMoreVerses}
                                                    >
                                                        {loadingMoreVerses ? (
                                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                                                        ) : (
                                                            'Load More Verses'
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-[#94a3b8] text-center py-8">
                                                No related verses found for this word
                                            </p>
                                        )}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* Login Prompt Dialog */}
            <LoginPromptDialog
                isOpen={showLoginPrompt}
                onClose={() => setShowLoginPrompt(false)}
                featureName={loginPromptFeature}
            />
        </>
    );
};

export default WordAnalysisPanel;
